/**
 * Per-tab guest marker.
 *
 * Stored in `sessionStorage` so it is scoped to THIS browser tab — it survives
 * reloads within the tab but is invisible to other tabs. This is deliberately
 * different from the auth session (`inmeet_auth_session`), which lives in
 * `localStorage` and is therefore shared across every tab.
 *
 * Why it exists: a user can be logged in in one tab and open a guest meeting
 * link in another. The second tab still hydrates the shared auth session, so
 * the app would otherwise treat the guest tab as that logged-in user and fire
 * authenticated-only calls (e.g. user-wide presence → 401/502). This flag lets
 * the guest tab opt out of those features WITHOUT clearing the shared session
 * and logging the other tab out.
 */

const GUEST_SESSION_KEY = 'inmeet_guest_session'

/** Mark the current tab as a guest session (set when a guest join is committed). */
export function markGuestSession(): void {
  try {
    sessionStorage.setItem(GUEST_SESSION_KEY, '1')
  } catch {
    /* storage unavailable (private mode / disabled) — best effort */
  }
}

/** True when this tab was opened/joined as a guest. */
export function isGuestSession(): boolean {
  try {
    return sessionStorage.getItem(GUEST_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/** Clear the guest marker — called when a real login takes over this tab. */
export function clearGuestSession(): void {
  try {
    sessionStorage.removeItem(GUEST_SESSION_KEY)
  } catch {
    /* noop */
  }
}
